import { Controller } from "@hotwired/stimulus";

// Keeps the close/reopen button in the comment box honest about what it does.
// The button and the textarea submit together, so once anything is typed the
// label says so ("Close with comment") rather than implying the draft is about
// to be thrown away. Same idiom as GitHub's comment box, which is where a
// reader's expectation comes from.
//
// Usage:
//   <form data-controller="comment-form"
//         data-comment-form-idle-label-value="Close"
//         data-comment-form-comment-label-value="Close with comment">
//     <textarea data-comment-form-target="body"
//               data-action="input->comment-form#sync"></textarea>
//     <button data-comment-form-target="stateButton">Close</button>
//   </form>
export default class extends Controller {
  static targets = ["body", "stateButton"];
  static values = { idleLabel: String, commentLabel: String };

  connect() {
    this.sync();
    // The vuln page morph-refreshes on a timer while an agent is working. A
    // morph keeps the typed draft but re-renders the button from the server
    // with its idle label, so re-sync instead of leaving a stale label until
    // the next keystroke.
    this.onMorph = () => this.sync();
    document.addEventListener("turbo:morph", this.onMorph);
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.onMorph);
  }

  sync() {
    if (!this.hasBodyTarget || !this.hasStateButtonTarget) return;
    const typed = this.bodyTarget.value.trim().length > 0;
    this.stateButtonTarget.textContent = typed
      ? this.commentLabelValue
      : this.idleLabelValue;
  }

  // Ctrl/Cmd+Enter posts the comment — the same muscle memory as GitHub's
  // box. requestSubmit() with no submitter carries no close_action, so the
  // shortcut can only ever comment; closing stays a deliberate click. An
  // empty draft is ignored rather than posted as a no-op request.
  submitShortcut(e) {
    if (e.key !== "Enter" || !(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    if (this.bodyTarget.value.trim() === "") return;
    this.element.requestSubmit();
  }

  // Inserts @dora at the caret (replacing any selection) — the button half of
  // the mention pill the thread renders. type=button, so it never submits.
  tag(e) {
    const body = this.bodyTarget;
    const mention = "@dora ";
    const at = body.selectionStart ?? body.value.length;
    const end = body.selectionEnd ?? at;
    body.setRangeText(mention, at, end, "end");
    body.focus();
    this.sync();
  }
}
